/**
 * Custom Auth Hook - SIMPLIFIED
 *
 * Basic Auth0 authentication without organization complexity
 * Re-exports Auth0 hook with helpful utilities
 */

import { useAuth0 } from '@auth0/auth0-react';
import { getAuth0Config } from './config';

export function useAuth() {
  const auth0 = useAuth0();
  const config = getAuth0Config();

  /**
   * Get access token for API calls
   * Automatically includes audience
   */
  const getAccessToken = async () => {
    try {
      return await auth0.getAccessTokenSilently({
        authorizationParams: {
          audience: config.audience
        }
      });
    } catch (error) {
      console.error('Error getting access token:', error);
      throw error;
    }
  };

  /**
   * Make authenticated API call
   * Automatically includes access token in Authorization header
   */
  const fetchWithAuth = async (url: string, options: RequestInit = {}) => {
    const token = await getAccessToken();

    return fetch(url, {
      ...options,
      headers: {
        ...options.headers,
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
  };

  return {
    // Auth0 core methods
    ...auth0,

    // Custom helpers
    getAccessToken,
    fetchWithAuth,

    // Convenient aliases
    user: auth0.user,
    isAuthenticated: auth0.isAuthenticated,
    isLoading: auth0.isLoading,
    error: auth0.error
  };
}

/**
 * Type for the auth context
 */
export type UseAuthReturn = ReturnType<typeof useAuth>;
